/**
 * Job Recovery utilities for resuming video generation from URL
 */

import { videoApiClient, JOB_STATES } from '../services/videoApi';

const normalizeRecoveryJobState = (jobStatus) => {
  const rawState = String(jobStatus?.state || '').toLowerCase();

  switch (rawState) {
    case 'completed':
    case 'done':
    case 'ready':
    case 'success':
    case 'succeeded':
      return JOB_STATES.COMPLETED;
    case 'failed':
    case 'error':
    case 'cancelled':
    case 'canceled':
    case 'rejected':
      return JOB_STATES.FAILED;
    case 'generating':
    case 'rendering':
    case 'processing':
    case 'running':
      return JOB_STATES.GENERATING;
    case 'draft':
    // The video API displays Creative Studio / director jobs (ACCEPTED with a
    // director-source binding) as 'draft' via _display_job_state. Treat it as a
    // pre-generation configuration state so recovery lands on the Configure step.
    case 'queued':
    case 'pending':
    case 'accepted':
      return JOB_STATES.ACCEPTED;
    case 'scraping':
      return JOB_STATES.SCRAPING;
    case 'data_ready':
      return JOB_STATES.DATA_READY;
    default:
      if (jobStatus?.assets?.video_url || jobStatus?.completedAt || jobStatus?.completed_at) {
        return JOB_STATES.COMPLETED;
      }
      return rawState || null;
  }
};

/**
 * Recovery states for different scenarios
 */
export const RECOVERY_STATES = {
  LOADING: 'loading',
  SUCCESS: 'success',
  NOT_FOUND: 'not_found',
  EXPIRED: 'expired',
  NETWORK_ERROR: 'network_error',
  INVALID_JOB_ID: 'invalid_job_id'
};

/**
 * Maps API job states to UI steps (4-step workflow)
 * Step 1: Find Business
 * Step 2: Configure
 * Step 3: Generate Video
 * Step 4: Review & Launch
 */
export const jobStateToStep = (jobState) => {
  switch (normalizeRecoveryJobState({ state: jobState })) {
    case JOB_STATES.ACCEPTED:
    case JOB_STATES.SCRAPING:
    case JOB_STATES.DATA_READY:
      return 2; // Configure step - scraping done, ready for user configuration
    case JOB_STATES.GENERATING:
    case JOB_STATES.RENDERING:
      return 3; // Generation/loading step - actively generating video
    case JOB_STATES.COMPLETED:
      return 4; // Success step - video ready
    case JOB_STATES.FAILED:
      return 3; // Stay on generation step but show error
    default:
      return 1; // Default to search step
  }
};

/**
 * Maps API job states to loading status
 * Only GENERATING and RENDERING are true loading states
 * SCRAPING/DATA_READY mean the user is on the configure step
 */
export const jobStateToLoadingStatus = (jobState) => {
  const normalizedJobState = normalizeRecoveryJobState({ state: jobState });
  const loadingStates = [
    JOB_STATES.GENERATING,
    JOB_STATES.RENDERING
  ];
  return loadingStates.includes(normalizedJobState);
};

/**
 * Checks if job is in configuration/review phase (post-scraping, pre-generation)
 */
export const isConfigurationPhase = (jobState) => {
  const normalizedJobState = normalizeRecoveryJobState({ state: jobState });
  const configStates = [
    JOB_STATES.ACCEPTED,
    JOB_STATES.SCRAPING,
    JOB_STATES.DATA_READY
  ];
  return configStates.includes(normalizedJobState);
};

/**
 * Maps API job states to completion status
 */
export const jobStateToCompletionStatus = (jobState) => {
  return normalizeRecoveryJobState({ state: jobState }) === JOB_STATES.COMPLETED;
};

/**
 * Extracts business information from job status for UI
 */
export const extractBusinessInfoFromJob = (jobStatus) => {
  if (!jobStatus.business) {
    return null;
  }

  const business = jobStatus.business;
  return {
    name: business.name || '',
    address: business.address || '',
    placeId: business.place_id || '', // May not always be available
    rating: business.stars || 0,
    userRatingsTotal: business.review_count || 0,
    // Map other fields as needed
  };
};

/**
 * Recovers job state and determines UI state
 */
export const recoverJobState = async (jobId) => {
  const result = {
    state: RECOVERY_STATES.LOADING,
    jobStatus: null,
    businessInfo: null,
    uiState: {
      currentStep: 1,
      isLoading: false,
      isComplete: false,
      progress: 0,
      currentStatus: 0,
      loadingMessage: '',
      generationError: null,
      videoUrl: null
    },
    error: null
  };

  try {
    // Fetch job status from API
    const jobStatus = await videoApiClient.getJobStatus(jobId);
    result.jobStatus = jobStatus;
    result.state = RECOVERY_STATES.SUCCESS;

    // Extract business information
    result.businessInfo = extractBusinessInfoFromJob(jobStatus);

    // Determine UI state based on job state
    const normalizedState = normalizeRecoveryJobState(jobStatus);
    const currentStep = jobStateToStep(normalizedState);
    const isLoading = jobStateToLoadingStatus(normalizedState);
    const isComplete = jobStateToCompletionStatus(normalizedState);

    result.uiState = {
      currentStep,
      isLoading,
      isComplete,
      progress: jobStatus.progress?.percent || 0,
      currentStatus: mapJobStateToStatusIndex(normalizedState),
      loadingMessage: getLoadingMessageForState(normalizedState),
      generationError: normalizedState === JOB_STATES.FAILED ? {
        title: "Video generation was interrupted",
        message: extractErrorMessage(jobStatus),
        jobId: jobId
      } : null,
      videoUrl: jobStatus.assets?.video_url || null
    };

  } catch (error) {
    // Handle different error scenarios
    if (error.statusCode === 404) {
      result.state = RECOVERY_STATES.NOT_FOUND;
      result.error = 'Job not found. It may have expired or been deleted.';
    } else if (error.statusCode === 0) {
      result.state = RECOVERY_STATES.NETWORK_ERROR;
      result.error = 'Unable to connect to the video service. Please check your connection.';
    } else {
      result.state = RECOVERY_STATES.NETWORK_ERROR;
      result.error = error.message || 'An unexpected error occurred while recovering the job.';
    }
  }

  return result;
};

/**
 * Maps job state to status indicator index
 */
const mapJobStateToStatusIndex = (jobState) => {
  const normalizedJobState = normalizeRecoveryJobState({ state: jobState });
  const statusMapping = {
    [JOB_STATES.ACCEPTED]: 0,
    [JOB_STATES.SCRAPING]: 0,
    [JOB_STATES.DATA_READY]: 1,
    [JOB_STATES.GENERATING]: 2,
    [JOB_STATES.RENDERING]: 3,
    [JOB_STATES.COMPLETED]: 4,
    [JOB_STATES.FAILED]: 0
  };
  return statusMapping[normalizedJobState] || 0;
};

/**
 * Gets appropriate loading message for job state
 */
const getLoadingMessageForState = (jobState) => {
  const normalizedJobState = normalizeRecoveryJobState({ state: jobState });
  const messages = {
    [JOB_STATES.ACCEPTED]: 'Starting video generation...',
    [JOB_STATES.SCRAPING]: 'Collecting venue details',
    [JOB_STATES.DATA_READY]: 'Preparing video workflow',
    [JOB_STATES.GENERATING]: 'Generating content',
    [JOB_STATES.RENDERING]: 'Rendering video',
    [JOB_STATES.COMPLETED]: 'Video generation completed!',
    [JOB_STATES.FAILED]: 'Video generation failed'
  };
  return messages[normalizedJobState] || 'Processing...';
};

/**
 * Extracts error message from failed job status
 */
const extractErrorMessage = (jobStatus) => {
  if (!jobStatus.errors || !Array.isArray(jobStatus.errors)) {
    return 'Something interrupted the video generation.';
  }

  return jobStatus.errors
    .map(error => error.message || error.detail || JSON.stringify(error))
    .join(' ') || 'Something interrupted the video generation.';
};

/**
 * Continues polling for a job that's still in progress
 */
export const continueJobPolling = async (jobId, onProgress) => {
  try {
    const result = await videoApiClient.pollJobStatus(jobId, {
      pollInterval: 2000,
      onProgress
    });
    return result;
  } catch (error) {
    throw error;
  }
};

/**
 * Validates if a job can be recovered (not too old, valid format, etc.)
 */
export const canRecoverJob = (jobId) => {
  // Basic validation - can be extended with more rules
  if (!jobId || typeof jobId !== 'string') {
    return false;
  }

  // Check job ID format - accept either UUID or vid_ prefixed format
  const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  const vidPattern = /^vid_[A-Z0-9]+$/;
  if (!uuidPattern.test(jobId) && !vidPattern.test(jobId)) {
    return false;
  }

  // Could add additional checks like:
  // - Job age validation
  // - User permission checks
  // - Rate limiting

  return true;
};

/**
 * Debug utilities for job recovery
 */
export const debugJobRecovery = {
  logRecoveryAttempt: (jobId, result) => {
    if (process.env.NODE_ENV === 'development') {
      console.debug(`[Job Recovery] Attempted to recover job ${jobId}:`, {
        state: result.state,
        jobState: result.jobStatus?.state,
        uiStep: result.uiState.currentStep,
        error: result.error,
        timestamp: new Date().toISOString()
      });
    }
  },

  logRecoveryError: (jobId, error) => {
    if (process.env.NODE_ENV === 'development') {
      console.error(`[Job Recovery] Failed to recover job ${jobId}:`, error);
    }
  }
};
